/**
 * JsonView — a read-only, pretty-printed JSON block for the audit-journal before/after diffs and the
 * subscription/projection raw view. Mono face, scrollable, token-styled, LTR (JSON is code, not prose).
 * The journal carries metadata diffs only — services that touch PHI stamp diffs, not bodies (L-4) — so
 * this never renders patient data; it renders whatever the audit hook persisted.
 */
import type { ReactNode } from "react";

export interface JsonViewProps {
  value: unknown;
  /** Shown when value is null/undefined. */
  empty?: ReactNode;
  maxHeight?: number;
}

export function JsonView({ value, empty = "—", maxHeight = 240 }: JsonViewProps) {
  if (value == null) {
    return <span style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{empty}</span>;
  }
  let text: string;
  try {
    text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre
      dir="ltr"
      style={{
        margin: 0,
        padding: "var(--nv-s-3)",
        maxHeight,
        overflow: "auto",
        background: "var(--nv-bg-alt, var(--nv-surf-up))",
        border: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
        borderRadius: "var(--nv-r-sm)",
        fontFamily: "var(--nv-font-mono)",
        fontSize: "var(--nv-fs-caption)",
        color: "var(--nv-text-1)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text}
    </pre>
  );
}
